/* GET /api/spans and /api/spans/{id} - 15-api-contract.md section 3.3, read
 * from the mirror. A span reads as one of its jobs' readings, the worse
 * measured verdict (20-schema.md section 3.9a), chosen by the server so the
 * list, its sort, its counts and the detail's headline cannot disagree.
 */
import { keepPreviousData, queryOptions, useQuery } from "@tanstack/react-query";
import { fetchJson, fetchPage, useAllPages, withQuery, type PageArgs, type QueryParams } from "./envelope";
import type { ConditionBand, SpanDetailRecord, SpanFacets, SpanRecord } from "./types";

/* A band chip's value as the API takes it: Kanopy's verdict on the reading
 * the span reads as. */
export type SpanBandChip = ConditionBand | "Unmeasured";

/* The filters the Spans screen sends, all served by `/api/spans`. The route
 * ignores a parameter it does not declare, so a filter sent but not served
 * would return every span. */
export interface MirrorSpanFilters {
  capture_round_id?: string;
  capture_id?: string;
  search?: string; // either end pole's Kanopy id (15 section 3.3)
  verdict_band?: SpanBandChip[];
}

/* The sorts `/api/spans` serves, and the direction. */
export type SpanSort = "encroaching" | "project" | "band" | "length" | "trees" | "min_veg" | "mean_veg" | "pruning_volume";
export interface SpanOrder {
  sort: SpanSort;
  order: "asc" | "desc";
}

/* The round, job and search both list requests share, as query parameters. */
function scope(filters: MirrorSpanFilters): QueryParams {
  return { capture_round_id: filters.capture_round_id, capture_id: filters.capture_id, search: filters.search };
}

export function useMirrorSpans(
  filters: MirrorSpanFilters,
  { sort, order }: SpanOrder,
  { cursor = null, limit }: PageArgs = {},
  { enabled = true }: { enabled?: boolean } = {},
) {
  const params: QueryParams = { ...scope(filters), verdict_band: filters.verdict_band, sort, order };
  return useQuery({
    queryKey: ["mirror", "spans", params, cursor, limit],
    queryFn: () => fetchPage<SpanRecord>("/api/spans", params, cursor, limit),
    enabled,
  });
}

/* Every span of one round, for a choice offered from the whole of it: a
 * missed tree placed on its project's latest round (27 section 8). Null holds
 * the read. */
export function useRoundSpans(roundId: string | null) {
  return useAllPages<SpanRecord>(["mirror", "spans", "round"], "/api/spans", { capture_round_id: roundId ?? undefined }, roundId !== null);
}

/* How many spans each band chip would match, under the same round, job and
 * search as the list. */
export function useSpanFacets(filters: MirrorSpanFilters, { enabled = true }: { enabled?: boolean } = {}) {
  const params = scope(filters);
  return useQuery({
    queryKey: ["mirror", "span-facets", params],
    queryFn: () =>
      fetchJson<SpanFacets>(withQuery("/api/spans/facets", params), (status) => `the span counts could not be read (${status})`),
    enabled,
  });
}

/* One span from `/api/spans/{id}`, for the screens that name a span by its
 * poles: one at a time through `useMirrorSpan`, or several at once through
 * `useQueries`, as the field app names each tree's (field/trees.ts). */
export function spanQuery(id: string | null | undefined) {
  return queryOptions({
    queryKey: ["mirror", "span", id],
    queryFn: () =>
      fetchJson<SpanDetailRecord>(`/api/spans/${encodeURIComponent(id!)}`, (status) => `the span could not be read (${status})`),
    enabled: id !== undefined && id !== null,
  });
}

/* One span: for Span detail, and for the screens that name a span by its
 * poles. The span before stays on screen while the next one loads, so
 * Previous and Next do not flash a skeleton. */
export function useMirrorSpan(id: string | null | undefined) {
  return useQuery({ ...spanQuery(id), placeholderData: keepPreviousData });
}
